import React from 'react';
import { Box, Typography, Button, Paper, Divider, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { CheckCircle, Error } from '@material-ui/icons';

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(4),
    maxWidth: '800px',
    margin: '0 auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  resultPaper: {
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
  },
  successSection: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  errorSection: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  successIcon: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
    fontSize: '32px',
  },
  errorIcon: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(1),
    fontSize: '32px',
  },
  countText: {
    fontSize: '20px',
    fontWeight: 600,
  },
  errorList: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
  },
  errorItem: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(6),
  },
}));

interface Props {
  data: any;
  onBack: () => void;
  onComplete: () => void;
  isImporting: boolean;
}

export function ImportedStep({ data, onBack, onComplete, isImporting }: Props) {
  const classes = useStyles();

  if (isImporting) {
    return (
      <Box className={classes.container}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography style={{ marginTop: '24px', fontSize: '18px' }}>Importing tasks...</Typography>
        </Box>
      </Box>
    );
  }

  const importedCount = data?.importedTasks?.length || 0;
  const skippedCount = data?.skippedTasks?.length || 0;

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Import Complete</Typography>

      <Paper className={classes.resultPaper}>
        <Box className={classes.successSection}>
          <CheckCircle className={classes.successIcon} />
          <Typography className={classes.countText}>{importedCount} tasks imported successfully</Typography>
        </Box>

        {skippedCount > 0 && (
          <>
            <Divider />
            <Box className={classes.errorSection}>
              <Error className={classes.errorIcon} />
              <Typography className={classes.countText}>{skippedCount} tasks skipped</Typography>
            </Box>
            <Box className={classes.errorList}>
              {data.skippedTasks.slice(0, 10).map((task: any) => (
                <Typography key={`${task.profileId}-${task.taskId}`} className={classes.errorItem}>
                  Profile: {task.profileId}, Task: {task.taskId} - {task.error || 'Error occurred'}
                </Typography>
              ))}
              {skippedCount > 10 && (
                <Typography className={classes.errorItem}>... and {skippedCount - 10} more</Typography>
              )}
            </Box>
          </>
        )}
      </Paper>

      <Box className={classes.buttonContainer}>
        <Button variant='outlined' onClick={onBack}>
          Import More
        </Button>
        <Button variant='contained' color='primary' onClick={onComplete}>
          Done
        </Button>
      </Box>
    </Box>
  );
}
